/* eslint-disable react/function-component-definition */
import React from 'react';

import Head from 'next/head';

import { Typography, Container, ContainerProps } from '@mui/material';
import classNames from 'classnames';

import { CommonMessages } from '@kamernet/core/CommonMessages';
import { useIntl } from '@kamernet/core/Intl';
import { Locale } from '@kamernet/core/Intl/types';

import { Footer } from './components/Footer';
import { Header } from './components/Header';
import { LoadingIndicator } from './components/LoadingIndicator';
import { SimpleHeader } from './components/SimpleHeader/SimpleHeader';
import styles from './PageLayout.module.css';

export interface PageLayoutProps {
  pageTitle: string;
  metaDescription?: string;
  seoLinksByLocale?: (locale: Locale) => React.JSX.Element;
  loading?: boolean;
  children: React.ReactNode;
  className?: string;
  ogImage?: string;
  simpleHeader?: boolean;
  hideFooter?: boolean;
}

export const PageLayout = ({
  pageTitle,
  metaDescription = undefined,
  seoLinksByLocale = undefined,
  loading = false,
  children,
  className = undefined,
  ogImage = undefined,
  simpleHeader = undefined,
  hideFooter = undefined,
}: PageLayoutProps) => {
  const { T, locale } = useIntl();

  const title = React.useMemo(() => {
    return `${pageTitle.replace(
      // eslint-disable-next-line custom/no-restricted-translation-concatenation-with-template-literal
      ` | ${T(CommonMessages.app_name)}`,
      '',
      // eslint-disable-next-line custom/no-restricted-translation-concatenation-with-template-literal
    )} | ${T(CommonMessages.app_name)}`;
  }, [T, pageTitle]);

  return (
    <React.Fragment>
      <Head>
        <title>{title}</title>
        {metaDescription && (
          <meta name="description" content={metaDescription} />
        )}
        {seoLinksByLocale?.(locale)}
        {ogImage && <meta property="og:image" content={ogImage} />}
      </Head>
      <div className={classNames(styles.root, className)}>
        {simpleHeader ? <SimpleHeader /> : <Header />}

        <LoadingIndicator loading={loading} />

        {children}

        {!hideFooter && <Footer />}
      </div>
    </React.Fragment>
  );
};

PageLayout.MainContainer = function PageLayoutMainContainer({
  children,
  ...props
}: Pick<React.HTMLAttributes<HTMLDivElement>, 'children' | 'className'> &
  Pick<ContainerProps, 'maxWidth'>) {
  return (
    <Container
      id="page-content"
      data-testid="PageContent"
      component="main"
      {...props}
    >
      {children}
    </Container>
  );
};

PageLayout.MainRaw = function PageLayoutMainRaw({
  children,
  className,
}: Pick<React.HTMLAttributes<HTMLDivElement>, 'children' | 'className'>) {
  return (
    <main id="page-content" data-testid="PageContent" className={className}>
      {children}
    </main>
  );
};

PageLayout.SectionRaw = function PageLayoutSectionRaw({
  id,
  'data-testid': testid,
  className,
  children,
}: Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'id' | 'className' | 'children'
> & {
  'data-testid'?: string;
}) {
  return (
    <section id={id} data-testid={testid} className={className}>
      {children}
    </section>
  );
};

PageLayout.SectionContainer = function PageLayoutSectionContainer({
  id,
  'data-testid': testid,
  className,
  containerClassName,
  children,
}: Pick<
  React.HTMLAttributes<HTMLDivElement>,
  'id' | 'className' | 'children'
> & {
  'data-testid'?: string;
  containerClassName?: React.HTMLAttributes<HTMLDivElement>['className'];
}) {
  return (
    <section id={id} data-testid={testid} className={className}>
      <Container className={containerClassName}>{children}</Container>
    </section>
  );
};

PageLayout.Title = function PageLayoutTitle({
  children,
  className,
}: Pick<React.HTMLAttributes<HTMLDivElement>, 'children' | 'className'>) {
  return (
    <Typography
      variant="h2"
      data-testid="PageTitle"
      className={classNames(styles.pageTitle, className)}
    >
      {children}
    </Typography>
  );
};

/**
 * Forces a full unmount/remount cycle whenever the `id` prop changes.
 *
 * Why:
 * - By default, Next.js reuses the same page component instance when
 *   only the dynamic route param changes (e.g. /post/1 → /post/2).
 * - Wrapping page content in this component with a `key` based on `id`
 *   ensures state and effects reset completely.
 * - `id` can be anything: an entity ID, page number, etc.
 * - Recommended usage: wrap {@link PageLayout.MainRaw} or {@link PageLayout.MainContainer}
 *   with RemountOnIDChange
 */
PageLayout.RemountOnIDChange = function PageLayoutRemountOnIDChange({
  children,
  id,
}: {
  children: React.ReactNode;
  id: React.Key;
}) {
  return <React.Fragment key={id}>{children}</React.Fragment>;
};
